// Pure helpers for the Fleet move — no React, so the Layout shell can import them cheaply.

/**
 * THE FLEET TAB MOVED (spec §9, owner 2026-10-04) — WITHOUT TAKING ANYTHING AWAY.
 * A user who holds /fleet-monitoring is sent from the old URLs to the new
 * page. A user who only holds /pos-config keeps the old Fleet tab and device
 * pages exactly as before (owner decision: no silent revocation) until the
 * owner grants Fleet Monitoring in Roles & Access (Phase 4, Task 30).
 */
export function fleetMonitoringTarget(search: string): string {
  const q = new URLSearchParams(search);
  q.delete('tab');
  const s = q.toString();
  return `/fleet-monitoring${s ? `?${s}` : ''}`;
}

type Level = 'view' | 'edit' | null;
const RANK: Record<string, number> = { view: 1, edit: 2 };

/**
 * Should this user be moved off the legacy fleet URLs? Only when they hold
 * /fleet-monitoring at a level AT LEAST their /pos-config level — a POS
 * Config editor with Fleet Monitoring view stays on the legacy (editable)
 * view rather than being downgraded to a view-only page. Owner: edit/edit.
 */
export function prefersFleetMonitoring(access: { hasAccess: (p: string) => boolean; levelForPath: (p: string) => Level }): boolean {
  if (!access.hasAccess('/fleet-monitoring')) return false;
  const fm = RANK[access.levelForPath('/fleet-monitoring') ?? ''] ?? 0;
  const pc = RANK[access.levelForPath('/pos-config') ?? ''] ?? 0;
  return fm >= pc;
}

/**
 * The legacy URLs that are really Fleet: `/pos-config/fleet/:id` and
 * `/pos-config?tab=fleet`. The Layout shell admits a /fleet-monitoring holder
 * to these (so the gates below can redirect them) without granting the rest
 * of /pos-config — no ROUTE_ALIASES entry, which would revoke POS-Config-only users.
 */
export function isLegacyFleetUrl(pathname: string, search: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  if (/^\/pos-config\/fleet\/[^/]+$/.test(p)) return true;
  return p === '/pos-config' && new URLSearchParams(search).get('tab') === 'fleet';
}

export function devicePageTarget(deviceId: string, search: string): string {
  const s = new URLSearchParams(search).toString();
  return `/fleet-monitoring/devices/${encodeURIComponent(deviceId)}${s ? `?${s}` : ''}`;
}
